import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Link } from 'react-router-dom';
import Confetti from 'react-confetti';
import { 
  RiFlowChart, RiImage2Line, RiGitBranchLine, RiRocket2Line, 
  RiRadarLine, RiShieldCheckLine, RiSearchEyeLine, RiMagicLine, 
  RiLineChartLine, RiShieldStarLine, RiBarChartBoxLine, RiBrushLine, 
  RiMegaphoneLine, RiLayoutGridLine, RiFocus2Line, RiCodeBoxLine, 
  RiDatabase2Line, RiBugLine, RiArrowLeftLine 
} from 'react-icons/ri';
import SEO from '../components/common/SEO';
import FAQSection from '../components/common/FAQSection';
import LeadForm from '../components/LeadForm';
import { trackEvent } from '../lib/analytics';



// --- Utility Components ---
function ZoomableImage({ src, alt }) {
  const [zoom, setZoom] = React.useState(1);
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });
  const MIN = 1; const MAX = 4;
  const clampZoom = (z) => Math.min(MAX, Math.max(MIN, z));

  React.useEffect(() => { setZoom(1); setOffset({ x: 0, y: 0 }); }, [src]);

  const handleWheel = React.useCallback((e) => {
    e.preventDefault();
    setZoom((z) => {
      const next = clampZoom(z - e.deltaY * 0.001 * z);
      if (next === MIN) setOffset({ x: 0, y: 0 });
      return next;
    });
  }, []);

  return (
    <div
      onWheel={handleWheel}
      style={{ position: 'relative', background: '#1e293b', overflow: 'hidden', height: 'calc(90vh - 130px)', cursor: zoom > 1 ? 'grab' : 'default' }}
    >
      <motion.div
        drag={zoom > 1}
        dragConstraints={{ left: -800, right: 800, top: -500, bottom: 500 }}
        dragElastic={0.04}
        animate={{ scale: zoom, x: offset.x, y: offset.y }}
        transition={{ type: 'spring', stiffness: 320, damping: 32 }}
        style={{ position: 'absolute', inset: 0, transformOrigin: 'center center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        onDragEnd={(_, info) => setOffset(o => ({ x: o.x + info.offset.x, y: o.y + info.offset.y }))}
      >
        <AnimatePresence mode="wait">
          <motion.img
            key={src}
            src={src}
            alt={alt}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            draggable={false}
            style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain', userSelect: 'none', pointerEvents: 'none', background: 'white', position: 'absolute', inset: 0 }}
          />
        </AnimatePresence>
      </motion.div>

      <div style={{ position: 'absolute', bottom: '14px', right: '14px', zIndex: 20, display: 'flex', gap: '6px', alignItems: 'center' }}>
        {[
          { label: '−', fn: () => setZoom(z => { const n = clampZoom(z - 0.5); if (n === MIN) setOffset({ x: 0, y: 0 }); return n; }) },
          { label: `${Math.round(zoom * 100)}%`, fn: () => { setZoom(1); setOffset({ x: 0, y: 0 }); } },
          { label: '+', fn: () => setZoom(z => clampZoom(z + 0.5)) },
        ].map(({ label, fn }) => (
          <button key={label} type="button" onClick={fn}
            style={{ minWidth: '36px', height: '34px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(15,23,42,0.88)', color: 'white', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', padding: '0 10px' }}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function useCountUp(target, duration = 700) {
  const [display, setDisplay] = React.useState(target);
  const prev = React.useRef(target);
  React.useEffect(() => {
    const start = prev.current;
    const diff = target - start;
    if (diff === 0) return;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(start + diff * e));
      if (p < 1) requestAnimationFrame(tick);
      else prev.current = target;
    };
    requestAnimationFrame(tick);
  }, [target, duration]);
  return display;
}

function AEOAudit() {
  const [activePlatformFeature, setActivePlatformFeature] = useState(0);
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = React.useState(window.innerHeight);
  React.useEffect(() => {
    const handler = () => { setWindowWidth(window.innerWidth); setWindowHeight(window.innerHeight); };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const isMobile = windowWidth < 768;

  // --- ROI State ---
  const calcRef = useRef(null);
  const [roiRate, setRoiRate] = useState(120);
  const [roiProjects, setRoiProjects] = useState(15);
  const [roiTasks, setRoiTasks] = useState({ scrape: true, schema: true, eeat: true, gap: true, plan: true });
  const [confettiActive] = useState(false);
  const [, setRoiTouched] = useState(false);

  const TASK_DEFS = {
    scrape:  { label: 'Share-of-Model Scraping', trad: 40, agent: 2, icon: <RiRadarLine size={24} color="#38bdf8" /> },
    eeat:    { label: 'E-E-A-T Scoring',         trad: 24, agent: 1, icon: <RiBarChartBoxLine size={24} color="#a78bfa" /> },
    schema:  { label: 'Schema Validation',       trad: 16, agent: 1, icon: <RiCodeBoxLine size={24} color="#34d399" /> },
    gap:     { label: 'Citation Gap Analysis',   trad: 20, agent: 2, icon: <RiSearchEyeLine size={24} color="#fbbf24" /> },
    plan:    { label: 'Fix Plan Generation',     trad: 10, agent: 1, icon: <RiFlowChart size={24} color="#f87171" /> },
  };

  const toggleTask = (k) => {
    setRoiTasks(prev => {
      const nextState = { ...prev, [k]: !prev[k] };
      trackEvent('aeo_roi_task_toggled', { task: k, selected: nextState[k] });
      return nextState;
    });
    setRoiTouched(true);
  };

  const savedHrsPerProject = Object.entries(roiTasks).filter(([, v]) => v).reduce((acc, [k]) => acc + TASK_DEFS[k].trad - TASK_DEFS[k].agent, 0);
  const dollarSavedPerYear = Math.round(savedHrsPerProject * roiProjects * roiRate);
  const weeksSavedPerYear = Math.round((savedHrsPerProject * roiProjects) / 40);
  const totalTradHrs = Object.values(TASK_DEFS).reduce((a, t) => a + t.trad, 0);
  const selTradHrs = Object.entries(roiTasks).filter(([,v]) => v).reduce((a, [k]) => a + TASK_DEFS[k].trad, 0);
  const ttmReduction = Math.round((selTradHrs / totalTradHrs) * 100) || 0;

  const animDollar = useCountUp(dollarSavedPerYear);
  const animWeeks  = useCountUp(weeksSavedPerYear);
  const animTTM    = useCountUp(ttmReduction);
  const fmtLive = (n) => n >= 1000000 ? `$${(n/1000000).toFixed(1)}M` : `$${Math.round(n/1000)}K`;

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // --- Platform Features ---
  const platformFeatures = [
    { title: 'Share-of-Model Discovery', desc: 'Identify your true visibility inside LLMs like Gemini, ChatGPT, and Perplexity vs your named competitors.', img: '/images/aeo-sample/share-of-model.png', alt: 'Share of Model', tag: 'Discovery' },
    { title: 'Five-Dimension Scorecard', desc: 'Break down your AEO readiness across Schema, Content, Technical, Trust, and Search configurations.', img: '/images/aeo-sample/scorecard.png', alt: 'AEO Scorecard', tag: 'Deep Scan' },
    { title: 'Schema & E-E-A-T Fixing', desc: 'Every failing check is paired with a ready-to-deploy fix, like auto-generated JSON-LD schema blocks.', img: '/images/aeo-sample/crawler-schema.png', alt: 'Schema Validation', tag: 'Remediation' },
  ];

  // --- Unified Capabilities ---
  const unifiedCapabilities = [
    {
      title: 'AEO Desk, 24/7',
      desc: 'Bring search visibility issues whenever you need them. Use the workspace for live schema generation, citation analysis, and rank mapping without waiting for an agency audit.',
      icon: <RiRadarLine size={22} />,
      tint: '#0ea5e9',
      bg: 'linear-gradient(135deg, rgba(14,165,233,0.18), rgba(37,99,235,0.1))',
    },
    {
      title: 'Share-of-Model Metrics',
      desc: 'Measure exactly how often Answer Engines name, recommend, or cite your brand compared to competitors across high-intent prompts.',
      icon: <RiBarChartBoxLine size={22} />,
      tint: '#2563eb',
      bg: 'linear-gradient(135deg, rgba(37,99,235,0.16), rgba(99,102,241,0.1))',
    },
    {
      title: 'JSON-LD Generation',
      desc: 'Generate perfect, validate Schema.org JSON-LD blocks for articles, products, organizations, and FAQs specifically tuned for LLM scrapers.',
      icon: <RiCodeBoxLine size={22} />,
      tint: '#7c3aed',
      bg: 'linear-gradient(135deg, rgba(124,58,237,0.16), rgba(168,85,247,0.08))',
    },
    {
      title: 'E-E-A-T Gap Analysis',
      desc: 'Analyze your content for Experience, Expertise, Authoritativeness, and Trustworthiness against Google and OpenAI’s specific rating guidelines.',
      icon: <RiShieldCheckLine size={22} />,
      tint: '#0891b2',
      bg: 'linear-gradient(135deg, rgba(8,145,178,0.16), rgba(6,182,212,0.08))',
    },
    {
      title: 'Citation Block Generation',
      desc: 'Create highly structured "answer blocks" designed explicitly to be easily digestible and retrievable by Perplexity and Gemini RAG systems.',
      icon: <RiSearchEyeLine size={22} />,
      tint: '#0f766e',
      bg: 'linear-gradient(135deg, rgba(15,118,110,0.16), rgba(20,184,166,0.08))',
    },
    {
      title: 'Technical Crawling',
      desc: 'Simulate Answer Engine bot behavior (like ChatGPT-User) to ensure your payloads are not blocked by accidental robots.txt rules or client-side rendering loops.',
      icon: <RiBugLine size={22} />,
      tint: '#1d4ed8',
      bg: 'linear-gradient(135deg, rgba(29,78,216,0.16), rgba(59,130,246,0.08))',
    },
    {
      title: 'Competitive Scraping',
      desc: 'Discover the exact sources LLMs use to construct answers about your competitors, and reverse-engineer their semantic footprint.',
      icon: <RiDatabase2Line size={22} />,
      tint: '#0369a1',
      bg: 'linear-gradient(135deg, rgba(3,105,161,0.16), rgba(14,165,233,0.08))',
    },
  ];

  const targetPersonas = [
    { title: 'VP of Marketing & SEO', desc: 'Protect brand visibility as search traffic shifts from traditional Google links to zero-click LLM answers.' },
    { title: 'Content Strategists', desc: 'Identify what E-E-A-T signals your content is missing before investing in large-scale article production.' },
    { title: 'Technical SEOs', desc: 'Automate tedious JSON-LD generation and answer-engine bot crawl validation.' },
  ];

  const workflowSteps = [
    { step: '01', title: 'Bring your queries', desc: 'Input the high-intent buyer questions you want to dominate.', cloud: <RiSearchEyeLine size={20} color="#FF9900" /> },
    { step: '02', title: 'Agent audits LLMs', desc: 'Simulates hundreds of prompts across Perplexity, Gemini, and ChatGPT.', cloud: <RiRadarLine size={20} color="#4285F4" /> },
    { step: '03', title: 'Deploy Fixes', desc: 'Implement the generated schemas and content blocks to close the citation gap.', cloud: <RiCodeBoxLine size={20} color="#34d399" /> },
  ];

  return (
    <>
      <SEO pathname="/agents/aeo" agentKey="AEO" />
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 9999 }}>
        <Confetti width={windowWidth} height={windowHeight} recycle={false} numberOfPieces={confettiActive ? 300 : 0} gravity={0.15} />
      </div>

      <main style={{ minHeight: '100vh', background: 'var(--color-bg-light)', color: '#0f172a', fontFamily: '"Inter", -apple-system, sans-serif' }}>
        
        {/* HERO */}
        <section style={{ paddingTop: '120px', paddingBottom: '80px', background: 'linear-gradient(180deg, rgba(248,250,252,1) 0%, #ffffff 100%)', position: 'relative', overflow: 'hidden' }}>
          <div className="container" style={{ position: 'relative', zIndex: 10, maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.1fr 0.9fr', gap: '60px', alignItems: 'center' }}>
              <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
                <Link to="/marketing#studios" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: '#64748b', textDecoration: 'none', marginBottom: '20px', fontSize: '0.95rem', fontWeight: 600, transition: 'all 0.2s ease' }} onMouseEnter={(e) => e.currentTarget.style.color = '#2563eb'} onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}>
                  <RiArrowLeftLine size={18} /> Back to Marketing &rarr; Website Audits
                </Link>
                <br style={{ display: 'block' }} />
                <span style={{ display: 'inline-block', padding: '6px 14px', borderRadius: '999px', background: 'rgba(37,99,235,0.1)', color: '#2563eb', fontSize: '0.8rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '24px' }}>
                  AEO & SEO Agent
                </span>
                <h1 style={{ fontSize: isMobile ? '2.8rem' : '4.2rem', fontWeight: 900, lineHeight: 1.05, letterSpacing: '-0.04em', color: '#0f172a', marginBottom: '24px' }}>
                  Dominate <span style={{ color: '#2563eb' }}>Answer Engines</span> autonomously.
                </h1>
                <p style={{ fontSize: '1.25rem', color: '#475569', lineHeight: 1.6, marginBottom: '32px', maxWidth: '600px' }}>
                  Measure Share-of-Model visibility, validate programmatic schemas, and generate E-E-A-T focused content strategies at scale without a traditional SEO agency.
                </p>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' }}>
                  <a href="https://app.eagentix.com/register" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('aeo_deploy_click')} style={{ background: '#0f172a', color: 'white', padding: '16px 32px', borderRadius: '12px', fontSize: '1.1rem', fontWeight: 700, textDecoration: 'none', boxShadow: '0 10px 25px -5px rgba(15,23,42,0.3)', transition: 'all 0.2s ease' }}>
                    Deploy AEO Agent
                  </a>
                  <button onClick={() => { trackEvent('aeo_contact_click'); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }} style={{ background: '#2563eb', color: 'white', padding: '16px 32px', borderRadius: '12px', fontSize: '1.1rem', fontWeight: 700, border: 'none', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 10px 25px -5px rgba(37,99,235,0.3)' }}>
                    Contact Us / Request Audit
                  </button>
                  <button onClick={() => { trackEvent('aeo_roi_click'); document.getElementById('roi-calculator').scrollIntoView({ behavior: 'smooth' }); }} style={{ background: 'white', color: '#0f172a', padding: '16px 32px', borderRadius: '12px', fontSize: '1.1rem', fontWeight: 700, border: '1px solid #cbd5e1', cursor: 'pointer', transition: 'all 0.2s ease' }}>
                    Calculate ROI
                  </button>
                </div>
                <div style={{ color: '#64748b', fontSize: '0.95rem', fontWeight: 600 }}>
                  ✓ 1-Day Audit Delivery • Zero Data Retention • At least 3 citation gaps surfaced
                </div>
              </motion.div>
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.2 }}>
                <div style={{ position: 'relative', width: '100%', paddingBottom: '100%' }}>
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', borderRadius: '40px', transform: 'rotate(-3deg)', boxShadow: '0 25px 50px -12px rgba(37,99,235,0.25)' }} />
                  <div style={{ position: 'absolute', inset: 0, background: '#ffffff', borderRadius: '40px', transform: 'rotate(3deg)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(37,99,235,0.1)' }}>
                    <img src="/images/aeo-sample/cover.png" alt="AEO Audit Agent" style={{ width: '80%', height: '80%', objectFit: 'contain' }} />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Answer Engine Ecosystem Trust Bar */}
        <section style={{ background: '#f8fafc', padding: '36px 0', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px', textAlign: 'center' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', marginBottom: '20px' }}>
              Optimized for Citation & Visibility Across All Leading Answer Engines
            </span>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              {[
                { name: 'ChatGPT / OpenAI', icon: '/images/icons/openai.svg', bg: 'rgba(16, 163, 127, 0.08)', border: 'rgba(16, 163, 127, 0.2)' },
                { name: 'Google Gemini', icon: '/images/icons/google.svg', bg: 'rgba(66, 133, 244, 0.08)', border: 'rgba(66, 133, 244, 0.2)' },
                { name: 'Anthropic Claude', icon: '/images/icons/anthropic.svg', bg: 'rgba(217, 119, 6, 0.08)', border: 'rgba(217, 119, 6, 0.2)' },
                { name: 'Microsoft Copilot', icon: '/images/icons/microsoft-copilot.svg', bg: 'rgba(14, 165, 233, 0.08)', border: 'rgba(14, 165, 233, 0.2)' },
                { name: 'DeepSeek R1', icon: '/images/icons/deepseek-color.svg', bg: 'rgba(99, 102, 241, 0.08)', border: 'rgba(99, 102, 241, 0.2)' },
              ].map((brand) => (
                <motion.div
                  key={brand.name}
                  whileHover={{ y: -3, scale: 1.02 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 20px',
                    borderRadius: '16px',
                    background: brand.bg,
                    border: `1px solid ${brand.border}`,
                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.04)',
                    color: '#0f172a',
                    fontWeight: 800,
                    fontSize: '1rem'
                  }}
                >
                  <img src={brand.icon} alt={brand.name} style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
                  <span>{brand.name}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ROI CALCULATOR */}
        <section id="roi-calculator" ref={calcRef} style={{ padding: isMobile ? '60px 0' : '100px 0', background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)', color: 'white' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <span style={{ color: '#38bdf8', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '2px', fontSize: '0.85rem' }}>Interactive Savings Calculator</span>
              <h2 style={{ fontSize: isMobile ? '2rem' : '3.2rem', fontWeight: 900, color: 'white', letterSpacing: '-0.03em', margin: '12px 0 16px' }}>Calculate Your Audit Savings</h2>
              <p style={{ fontSize: '1.15rem', color: '#94a3b8', maxWidth: '650px', margin: '0 auto' }}>Select the technical SEO and AEO tasks you want the agent to handle, and see the reduction in time-to-market compared to manual agency audits.</p>
            </div>

            {/* INPUT SLIDERS */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '32px', marginBottom: '40px', background: 'rgba(255,255,255,0.03)', padding: '28px 32px', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(12px)' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ fontSize: '0.95rem', fontWeight: 600, color: '#94a3b8' }}>Audits / Projects Per Year</span>
                  <span style={{ fontSize: '1.1rem', fontWeight: 800, color: '#38bdf8' }}>{roiProjects} Audits</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={roiProjects}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    setRoiProjects(val);
                    trackEvent('aeo_roi_projects_changed', { value: val });
                  }}
                  style={{ width: '100%', accentColor: '#38bdf8', cursor: 'pointer' }}
                />
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ fontSize: '0.95rem', fontWeight: 600, color: '#94a3b8' }}>Loaded Hourly SEO Rate</span>
                  <span style={{ fontSize: '1.1rem', fontWeight: 800, color: '#34d399' }}>${roiRate} / hr</span>
                </div>
                <input
                  type="range"
                  min="50"
                  max="300"
                  step="10"
                  value={roiRate}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    setRoiRate(val);
                    trackEvent('aeo_roi_rate_changed', { value: val });
                  }}
                  style={{ width: '100%', accentColor: '#34d399', cursor: 'pointer' }}
                />
              </div>
            </div>

            {/* TASK TOGGLES */}
            <div style={{ marginBottom: '48px' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 800, color: '#fbbf24', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '16px', textAlign: 'center' }}>Tasks the Agent Automates</div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(5, 1fr)', gap: '14px' }}>
                {Object.entries(TASK_DEFS).map(([k, t], idx) => {
                  const on = roiTasks[k];
                  const colors = ['#38bdf8', '#a78bfa', '#34d399', '#fbbf24', '#f87171'];
                  const color = colors[idx % colors.length];
                  return (
                    <motion.button key={k} onClick={() => toggleTask(k)} whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.97 }}
                      style={{ padding: '20px 12px', borderRadius: '20px', cursor: 'pointer', border: '2px solid', borderColor: on ? color : 'rgba(255,255,255,0.08)', background: on ? `linear-gradient(135deg, ${color}20 0%, ${color}08 100%)` : 'rgba(255,255,255,0.02)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', minHeight: '125px', justifyContent: 'center', transition: 'all 0.2s ease', boxShadow: on ? `0 10px 25px -5px ${color}30` : 'none' }}>
                      <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: on ? `linear-gradient(135deg, ${color}35, ${color}15)` : 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {React.cloneElement(t.icon, { size: 22, color: on ? color : '#64748b' })}
                      </div>
                      <div style={{ fontSize: '0.8rem', fontWeight: 700, color: on ? '#ffffff' : '#94a3b8', lineHeight: 1.3, textAlign: 'center' }}>{t.label}</div>
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {/* RESULTS */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '24px' }}>
              <div style={{ background: 'linear-gradient(145deg, #1e293b 0%, #0f172a 100%)', borderRadius: '24px', padding: '36px 28px', textAlign: 'center', border: '1px solid rgba(52, 211, 153, 0.3)', boxShadow: '0 20px 40px -15px rgba(52, 211, 153, 0.15)' }}>
                <div style={{ color: '#34d399', fontSize: '0.85rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1.5px' }}>SEO Agency Spend Saved</div>
                <div style={{ fontSize: '3.6rem', fontWeight: 900, color: '#ffffff', margin: '16px 0', lineHeight: 1 }}>{fmtLive(animDollar)}</div>
                <div style={{ color: '#94a3b8', fontSize: '0.95rem', fontWeight: 500 }}>per year based on {roiProjects} deep audits</div>
              </div>
              <div style={{ background: 'linear-gradient(145deg, #1e293b 0%, #0f172a 100%)', borderRadius: '24px', padding: '36px 28px', textAlign: 'center', border: '1px solid rgba(96, 165, 250, 0.3)', boxShadow: '0 20px 40px -15px rgba(96, 165, 250, 0.15)' }}>
                <div style={{ color: '#60a5fa', fontSize: '0.85rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1.5px' }}>Time-to-Market Reduction</div>
                <div style={{ fontSize: '3.6rem', fontWeight: 900, color: '#ffffff', margin: '16px 0', lineHeight: 1 }}>{animTTM}%</div>
                <div style={{ color: '#94a3b8', fontSize: '0.95rem', fontWeight: 500 }}>faster technical remediation</div>
              </div>
              <div style={{ background: 'linear-gradient(145deg, #1e293b 0%, #0f172a 100%)', borderRadius: '24px', padding: '36px 28px', textAlign: 'center', border: '1px solid rgba(167, 139, 250, 0.3)', boxShadow: '0 20px 40px -15px rgba(167, 139, 250, 0.15)' }}>
                <div style={{ color: '#a78bfa', fontSize: '0.85rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1.5px' }}>Execution Weeks Saved</div>
                <div style={{ fontSize: '3.6rem', fontWeight: 900, color: '#ffffff', margin: '16px 0', lineHeight: 1 }}>{animWeeks}</div>
                <div style={{ color: '#94a3b8', fontSize: '0.95rem', fontWeight: 500 }}>weeks of manual crawling eliminated</div>
              </div>
            </div>
          </div>
        </section>

        {/* PLATFORM INTERFACE */}
        <section style={{ padding: isMobile ? '48px 0' : '80px 0', background: 'white' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ textAlign: 'center', margin: '0 auto', maxWidth: '800px' }}>
              <span style={{ color: '#2563eb', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '0.85rem' }}>Eagentix Platform Interface</span>
              <h2 style={{ fontSize: isMobile ? '1.6rem' : '2.5rem', fontWeight: 800, color: '#0f172a', margin: '16px 0' }}>Technical Intelligence Meets Execution</h2>
            </div>
            <div style={{ marginTop: '64px', background: '#0f172a', borderRadius: '28px', overflow: 'hidden', boxShadow: 'rgba(0,0,0,0.5) 0 25px 50px -12px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(320px, 0.95fr) minmax(0, 1.25fr)' }}>
                <div style={{ padding: '36px 32px', borderRight: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', gap: '28px' }}>
                  <AnimatePresence mode="wait">
                    <motion.div key={platformFeatures[activePlatformFeature].title} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.28 }}>
                      <span style={{ display: 'inline-flex', padding: '7px 12px', borderRadius: '999px', background: 'rgba(56,189,248,0.2)', color: '#7dd3fc', fontSize: '0.78rem', fontWeight: 800, textTransform: 'uppercase' }}>{platformFeatures[activePlatformFeature].tag}</span>
                      <h3 style={{ fontSize: '1.7rem', fontWeight: 800, color: 'white', margin: '18px 0 12px' }}>{platformFeatures[activePlatformFeature].title}</h3>
                      <p style={{ color: '#cbd5e1', lineHeight: 1.75 }}>{platformFeatures[activePlatformFeature].desc}</p>
                    </motion.div>
                  </AnimatePresence>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    {platformFeatures.map((f, i) => (
                      <motion.button key={f.title} onClick={() => setActivePlatformFeature(i)}
                        style={{ width: i === activePlatformFeature ? '36px' : '10px', height: '10px', borderRadius: '999px', border: 'none', background: i === activePlatformFeature ? '#38bdf8' : '#475569', cursor: 'pointer', transition: 'all 0.3s' }} />
                    ))}
                  </div>
                </div>
                <div style={{ overflow: 'hidden', background: '#1e293b' }}>
                  <motion.div animate={{ x: `-${activePlatformFeature * 100}%` }} transition={{ duration: 0.8, ease: 'easeInOut' }} style={{ display: 'flex', width: '300%' }}>
                    {platformFeatures.map(f => (
                      <div key={f.title} style={{ width: '33.33%', flexShrink: 0 }}>
                        <img src={f.img} alt={f.alt} style={{ width: '100%', height: '100%', minHeight: '460px', objectFit: 'contain', background: 'white' }} />
                      </div>
                    ))}
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* AGENT CAPABILITIES */}
        <section style={{ padding: isMobile ? '48px 0' : '100px 0', background: 'radial-gradient(circle at top, #e0e7ff 0%, #f8fafc 42%, #ffffff 100%)' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <span style={{ color: '#2563eb', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '0.85rem' }}>Full Stack Capabilities</span>
              <h2 style={{ fontSize: isMobile ? '2rem' : '3.5rem', fontWeight: 900, color: '#0f172a', margin: '16px 0' }}>Go to the agent for <span style={{ color: '#2563eb' }}>real search</span> dominance</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: '22px' }}>
              {unifiedCapabilities.map((item, index) => (
                <div key={item.title} style={{ background: index === 0 ? 'linear-gradient(145deg, #111827 0%, #1e3a8a 100%)' : 'white', border: '1px solid rgba(148,163,184,0.14)', borderRadius: '24px', padding: '32px', boxShadow: 'rgba(15,23,42,0.08) 0 24px 40px -30px', gridColumn: index === 0 ? '1 / -1' : 'auto' }}>
                  <div style={{ width: '52px', height: '52px', borderRadius: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: item.tint, background: item.bg, marginBottom: '20px' }}>{item.icon}</div>
                  <h3 style={{ fontSize: index === 0 ? '1.8rem' : '1.25rem', fontWeight: 800, color: index === 0 ? 'white' : '#0f172a', margin: '0 0 12px' }}>{item.title}</h3>
                  <p style={{ fontSize: '1.05rem', color: index === 0 ? '#dbeafe' : '#475569', lineHeight: 1.7, margin: 0 }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* BUYERS + WORKFLOW */}
        <section style={{ padding: isMobile ? '48px 0' : '86px 0', background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)' }}>
          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            {/* Qualification Filter (§3.9) */}
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <span style={{ color: '#2563eb', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '0.85rem' }}>Qualification Filter</span>
              <h2 style={{ fontSize: isMobile ? '1.8rem' : '2.75rem', fontWeight: 800, color: '#0f172a', margin: '16px 0 18px', lineHeight: 1.1 }}>Is the AEO Audit Agent Right for Your Brand?</h2>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '28px', maxWidth: '1000px', margin: '0 auto 64px' }}>
              {/* IS FOR */}
              <div style={{ background: 'white', borderRadius: '24px', padding: '32px', border: '2px solid #10b981', boxShadow: '0 10px 30px rgba(16, 185, 129, 0.08)' }}>
                <div style={{ color: '#10b981', fontWeight: 800, fontSize: '1.2rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</div>
                  Who This IS For
                </div>
                <div style={{ display: 'grid', gap: '16px' }}>
                  {targetPersonas.map((p) => (
                    <div key={p.title} style={{ padding: '16px', borderRadius: '16px', background: '#f8fafc', border: '1px solid rgba(148,163,184,0.12)' }}>
                      <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#0f172a' }}>{p.title}</h4>
                      <p style={{ margin: '6px 0 0', color: '#475569', fontSize: '0.9rem', lineHeight: 1.6 }}>{p.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* IS NOT FOR */}
              <div style={{ background: 'white', borderRadius: '24px', padding: '32px', border: '1px solid #cbd5e1', boxShadow: '0 4px 12px rgba(0,0,0,0.04)' }}>
                <div style={{ color: '#ef4444', fontWeight: 800, fontSize: '1.2rem', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</div>
                  Who This Is NOT For
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '16px', color: '#64748b', fontSize: '0.95rem', lineHeight: 1.6 }}>
                  <li style={{ paddingLeft: '24px', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#ef4444', fontWeight: 800 }}>✕</span>
                    Sites relying on outdated 2018 keyword stuffing or link-farm tactics.
                  </li>
                  <li style={{ paddingLeft: '24px', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#ef4444', fontWeight: 800 }}>✕</span>
                    Brands without dedicated technical or content teams to deploy JSON-LD fixes.
                  </li>
                  <li style={{ paddingLeft: '24px', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#ef4444', fontWeight: 800 }}>✕</span>
                    Companies expecting instant organic rank boosts without structured data alignment.
                  </li>
                </ul>
              </div>
            </div>

            {/* HOW IT WORKS */}
            <div style={{ borderRadius: '30px', overflow: 'hidden', background: 'linear-gradient(145deg, #0f172a 0%, #1e40af 100%)', color: 'white', padding: '40px', maxWidth: '1000px', margin: '0 auto' }}>
              <span style={{ display: 'inline-flex', padding: '7px 12px', borderRadius: '999px', background: 'rgba(56,189,248,0.2)', color: '#7dd3fc', fontSize: '0.78rem', fontWeight: 800, textTransform: 'uppercase' }}>How It Works</span>
              <h3 style={{ fontSize: '2rem', fontWeight: 800, margin: '18px 0 24px' }}>From raw data to search dominance</h3>
              <div style={{ display: 'grid', gap: '20px' }}>
                {workflowSteps.map((step) => (
                  <div key={step.step} style={{ display: 'flex', gap: '20px', alignItems: 'center', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '20px' }}>
                    <div style={{ width: '64px', height: '64px', borderRadius: '18px', background: 'rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ color: '#38bdf8', fontWeight: 800, fontSize: '0.72rem' }}>{step.step}</span>
                      {step.cloud}
                    </div>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: 'white' }}>{step.title}</h4>
                      <p style={{ margin: '8px 0 0', color: '#cbd5e1', fontSize: '0.95rem' }}>{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* What the audit surfaces */}
        <section style={{ background: '#0f172a', padding: '80px 0', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <div className="container" style={{ maxWidth: '850px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ 
              background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)', 
              borderRadius: '24px', 
              padding: '48px', 
              border: '2px solid #38bdf8', 
              boxShadow: '0 25px 50px -12px rgba(56, 189, 248, 0.25)', 
              textAlign: 'center' 
            }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(56, 189, 248, 0.15)', border: '1px solid #38bdf8', marginBottom: '20px' }}>
                <RiShieldStarLine size={36} color="#38bdf8" />
              </div>
              <span style={{ color: '#38bdf8', fontWeight: 800, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', marginBottom: '12px' }}>
                What The Audit Surfaces
              </span>
              <h3 style={{ fontSize: '2.2rem', fontWeight: 900, color: 'white', letterSpacing: '-0.02em', marginBottom: '16px' }}>
                At least three actionable citation gaps, surfaced.
              </h3>
              <p style={{ color: '#cbd5e1', fontSize: '1.1rem', lineHeight: 1.7, maxWidth: '700px', margin: '0 auto 28px' }}>
                Within 24 hours the audit surfaces at least three high-value citation gaps on your domain and generates production-ready JSON-LD schemas to close them.
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', flexWrap: 'wrap', color: '#94a3b8', fontSize: '0.9rem', fontWeight: 600 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <RiShieldCheckLine size={20} color="#34d399" /> Scope agreed before we start
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <RiRadarLine size={20} color="#38bdf8" /> Share-of-Model Visibility
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <RiCodeBoxLine size={20} color="#fbbf24" /> Auto-Generated JSON-LD Schema
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CONTACT SECTION */}
        <section id="contact" className="section contact-section" style={{ padding: isMobile ? '60px 0' : '90px 0', borderTop: '1px solid rgba(148,163,184,0.16)', backgroundColor: '#f8fafc' }}>
          <div className="container" style={{ display: 'flex', flexWrap: 'wrap', gap: '4rem', justifyContent: 'space-between', alignItems: 'center', maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
            <div style={{ flex: '1 1 400px' }}>
              <span style={{ color: '#2563eb', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '0.85rem' }}>Contact Us</span>
              <h2 style={{ fontSize: isMobile ? '2rem' : '3rem', margin: '16px 0 1rem', color: '#0f172a', fontWeight: 800, lineHeight: 1.1 }}>Ready to dominate <span style={{ color: '#2563eb' }}>Answer Engines</span>?</h2>
              <p style={{ fontSize: '1.2rem', marginBottom: '2rem', maxWidth: '500px', color: '#475569', lineHeight: '1.6' }}>
                Measure your Share-of-Model, fix schema & E-E-A-T gaps, and scale AI-search visibility. Let's discuss your brand's AEO strategy.
              </p>
              <div style={{ display: 'flex', gap: '2rem', marginTop: '2rem' }}>
                <div>
                  <strong style={{ display: 'block', color: '#0f172a', marginBottom: '0.5rem', fontSize: '1.1rem' }}>Sales & AEO Strategy</strong>
                  <a href="mailto:contact@eagentix.com" style={{ textDecoration: 'none', color: '#2563eb', fontWeight: 600 }}>contact@eagentix.com</a>
                </div>
              </div>
            </div>
            <div style={{ flex: '1 1 500px', maxWidth: '520px', width: '100%' }}>
              <LeadForm page="AEO Audit Agent" accent="#2563eb" title="Request an AEO Audit" subtitle="Tell us your domain and goals. We reply within 1 business day." buttonLabel="Request Audit" />

              {/* P.S. Section (§3.12) */}
              <div style={{ marginTop: '32px', padding: '20px 24px', borderRadius: '16px', background: 'white', border: '1px dashed #cbd5e1', textAlign: 'center' }}>
                <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: 1.6, margin: 0, fontStyle: 'italic' }}>
                  <strong style={{ color: '#2563eb', fontStyle: 'normal' }}>P.S.</strong> We audit your domain's Share-of-Model across ChatGPT, Perplexity, Claude, Copilot, and Gemini with zero web crawling footprints on your server.
                </p>
              </div>
            </div>
          </div>
        </section>

        <FAQSection agentKey="AEO" />
      </main>
    </>
  );
}

export default AEOAudit;
